import { WithRequired } from '@repo/common/interfaces';
import { AccessLevel, doesUserHaveAccess } from '@repo/common/schemas/models';
import { isTruthy, mapValues } from 'remeda';

export { AccessLevel };
export const hasAccess = doesUserHaveAccess;

interface BaseNavItem {
  title: string;
  icon?: string;
  accessLevel?: AccessLevel;

  isGroupTitle?: boolean;
  children?: readonly NavItem[];
}

interface NavItemLink extends BaseNavItem {
  link: string;
  isGroupTitle?: false;
  children?: never;
}
interface NavItemWithChildren extends BaseNavItem {
  children: readonly NavItem[];
  isGroupTitle?: false;
  link?: never;
}
interface NavGroupTitle extends BaseNavItem {
  isGroupTitle: true;
  children?: never;
  link?: never;
}

export type NavItem = Readonly<NavItemWithChildren | NavItemLink | NavGroupTitle>;
export type RootNavItem = WithRequired<NavItem, 'accessLevel' | 'icon'>;

export const dashboardMenuItems: Readonly<RootNavItem[]> = [
  {
    title: 'StaffIQ',
    icon: 'bi-inbox',
    link: '/staff-iq',
    accessLevel: 'user',
  },
  {
    title: 'DataIQ',
    icon: 'bi bi-database-up',
    link: '/data-iq/operations',
    accessLevel: 'manager',
  },
  {
    title: 'Analytics',
    icon: 'bi-pie-chart',
    link: '/analytics',
    accessLevel: 'manager',
  },
  {
    title: 'Settings',
    icon: 'bi-gear-wide-connected',
    link: '/settings',
    accessLevel: 'user',
  },
  {
    title: 'Toro Tools',
    icon: 'bi bi-tools',
    accessLevel: 'toroAdmin',
    children: [
      {
        title: 'StaffIQ (Demo)',
        link: '/demo/staff-iq',
      },
      {
        title: 'DataIQ (Demo)',
        link: '/demo/data-iq-config',
      },
      {
        title: 'Analytics (Demo)',
        link: '/demo/analytics',
      },
      {
        title: 'Card Tester',
        link: '/admin/card-tester/activity-iq',
      },
      {
        title: 'Data Evaluation',
        link: '/data-iq/eval',
      },
    ],
  },
  {
    title: 'Toro Admin',
    icon: 'bi-person-gear',
    accessLevel: 'toroAdmin',
    children: [
      {
        title: 'Account Management',
        link: '/admin/accounts',
      },
      {
        title: 'User Feedback',
        link: '/admin/user-feedback',
      },
      {
        title: 'Prompt Editor',
        link: '/admin/prompt-editor',
      },
      {
        title: 'Prompt Lab',
        link: '/admin/prompt-lab',
      },
      {
        title: 'System Health',
        link: '/admin/system-health',
      },
    ],
  },
];

export default dashboardMenuItems;

function filterNavItems<T extends NavItem>(items: readonly T[], level: AccessLevel): readonly T[] {
  return items
    .map((item) => {
      if (!hasAccess(item.accessLevel ?? 'public', level)) {
        return null;
      }
      if (item.children) {
        return {
          ...item,
          children: filterNavItems(item.children, level),
        };
      }
      return item;
    })
    .filter(isTruthy);
}

export const NAV_ITEMS_BY_ROLE: Readonly<Record<AccessLevel, readonly RootNavItem[]>> = mapValues(
  AccessLevel.enum,
  (role) => filterNavItems(dashboardMenuItems, role)
);
